import Button from '@/components/atoms/button';
import Modal from '@/components/molecules/modal';
import { useDisclosure } from '@/hooks';
import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useState } from 'react';

const ConfirmActionModal = forwardRef(
	(
		{
			onConfirm,
			onCancel,
			icon,
			title,
			contents,
			actionBtnText,
			confirmingBtnText,
			cancelBtnText,
			reverseBtnOrder,
			tableRef,
			showCancel,
			dialogFooterClassName,
			...props
		},
		ref
	) => {
		const { isOpen, onOpen, onClose } = useDisclosure(false);
		const [confirming, setConfirming] = useState(false);

		// expose open handler via ref
		useImperativeHandle(
			ref,
			() => {
				return {
					onOpen: onOpen,
				};
			},
			[onOpen]
		);

		const handleCancel = () => {
			onClose();

			if (typeof onCancel === 'function') onCancel();
		};

		const handleConfirm = () => {
			setConfirming(true);

			onConfirm(() => {
				setConfirming(false);
				onClose();

				// handle table refresh if a table ref is provided
				if (tableRef?.current?.refetch) tableRef.current.refetch();
			});
		};

		return (
			<Modal
				open={isOpen}
				onOpenChange={() => {
					if (isOpen) handleCancel();
				}}
				icon={icon}
				title={title}
				contents={contents}
				hideFooter
				closeDisabled={confirming}
				{...props}
			>
				{({ DialogFooter }) => (
					<DialogFooter
						disabled={confirming}
						className={dialogFooterClassName}
						{...(showCancel && {
							renderCustomCancel: () => (
								<Button
									variant="secondary"
									type="button"
									disabled={confirming}
									className="mt-4 md:mt-0"
									onClick={() => {
										// "action" to handle confirmed/submit confirmation
										if (reverseBtnOrder) {
											handleConfirm();
											return;
										}

										// "action" to handle cancel action
										handleCancel();
									}}
								>
									{reverseBtnOrder && confirming && confirmingBtnText ? confirmingBtnText : cancelBtnText}
								</Button>
							),
						})}
						renderCustomAction={() => (
							<Button
								variant="primary"
								type="button"
								disabled={confirming}
								onClick={() => {
									// "action" to handle cancelling confirmation
									if (reverseBtnOrder) {
										handleCancel();
										return;
									}

									// "action" to handle confirmed/submit action
									handleConfirm();
								}}
							>
								{!reverseBtnOrder && confirming && confirmingBtnText ? confirmingBtnText : actionBtnText}
							</Button>
						)}
					/>
				)}
			</Modal>
		);
	}
);

ConfirmActionModal.propTypes = {
	onConfirm: PropTypes.func.isRequired,
	onCancel: PropTypes.func,
	title: PropTypes.string,
	contents: PropTypes.string,
	actionBtnText: PropTypes.string,
	confirmingBtnText: PropTypes.string,
	cancelBtnText: PropTypes.string,
	reverseBtnOrder: PropTypes.bool,
	tableRef: PropTypes.object,
	showCancel: PropTypes.bool,
};

ConfirmActionModal.defaultProps = {
	icon: 'info',
	title: 'Are you sure',
	actionBtnText: 'Confirm',
	confirmingBtnText: 'Confirming...',
	cancelBtnText: 'Cancel',
	reverseBtnOrder: false,
	showCancel: true,
};

export default ConfirmActionModal;
